import type { GridRenderCellParams } from '@mui/x-data-grid';
import type { AdSetEntry } from '../api/getAdSets';
import { useFormContext, useWatch } from 'react-hook-form';
import type { MouseEvent } from 'react';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { Add, CopyAll } from '@mui/icons-material';
import { useInView } from 'react-intersection-observer';
import { format, isValid, parseISO } from 'date-fns';
import { parseUtc2AsLocal } from '../utils/startTime';

interface AdSetStartTimeFieldProps {
  cellParams: GridRenderCellParams<AdSetEntry>;
  onOpenDialog: (adSetId: string) => void;
}

const stopPropagation = (e: MouseEvent) => {
  e.stopPropagation();
};

const formatStartTime = (isoString: string): { date: string; time: string; localTime: string } => {
  try {
    const parsed = parseISO(isoString);
    if (!isValid(parsed)) return { date: '--', time: '--', localTime: '' };
    const localDate = parseUtc2AsLocal(isoString);
    const localTime = isNaN(localDate.getTime()) ? '' : format(localDate, 'dd.MM HH:mm');
    return { date: format(parsed, 'dd.MM.yy'), time: format(parsed, 'HH:mm'), localTime };
  } catch {
    return { date: '--', time: '--', localTime: '' };
  }
};

const AdSetStartTimeField = ({ cellParams, onOpenDialog }: AdSetStartTimeFieldProps) => {
  const currentId = cellParams.row.id;

  const { setValue, getValues } = useFormContext();

  const fieldName = `selectedAdSetsStartTimes.${currentId}`;
  const startTimeValue: string | undefined = useWatch({ name: fieldName });

  useEffect(() => {
    const currentValue = getValues(fieldName);
    if (!currentValue && cellParams.row.start_time) {
      setValue(fieldName, cellParams.row.start_time, { shouldValidate: true });
    }
  }, [fieldName, cellParams.row.start_time, getValues, setValue]);

  const handleOpenDialog = useCallback(() => {
    onOpenDialog(currentId);
  }, [onOpenDialog, currentId]);

  const handleApplyToAll = useCallback(() => {
    const currentStartTime: string | undefined = getValues(fieldName);
    if (!currentStartTime) return;

    const selectedAdSets: AdSetEntry[] = getValues('selectedAdSets');
    const allStartTimes: Record<string, string> = getValues('selectedAdSetsStartTimes');
    const updatedValues: Record<string, string> = { ...allStartTimes };

    selectedAdSets?.forEach((adSet) => {
      updatedValues[adSet.id] = currentStartTime;
    });

    setValue('selectedAdSetsStartTimes', updatedValues, { shouldValidate: true });
  }, [fieldName, getValues, setValue]);

  const { ref, inView } = useInView({ threshold: 0 });

  const hasStartTime = !!startTimeValue;

  const formatted = useMemo(
    () => (hasStartTime ? formatStartTime(startTimeValue) : null),
    [hasStartTime, startTimeValue],
  );

  return (
    <Box
      ref={ref}
      onClick={stopPropagation}
      sx={{
        position: 'relative',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1,
      }}
    >
      {inView && (
        <>
          {!hasStartTime && (
            <Button
              size="small"
              startIcon={<Add />}
              onClick={handleOpenDialog}
              sx={{ textTransform: 'initial' }}
            >
              Add
            </Button>
          )}

          {hasStartTime && formatted && (
            <>
              <Box
                onClick={handleOpenDialog}
                sx={{ cursor: 'pointer', lineHeight: 1.2 }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontSize: 12, color: 'text.disabled' }}
                >
                  {formatted.date}
                </Typography>
                <Typography variant="body2" sx={{ fontSize: 12 }}>
                  {formatted.time}
                </Typography>
              </Box>

              <Box className="MuiInputAdornment-positionEnd">
                <Button
                  size="small"
                  startIcon={<CopyAll />}
                  onClick={handleApplyToAll}
                  sx={{ textTransform: 'initial', px: 1 }}
                >
                  Apply to all
                </Button>
              </Box>
            </>
          )}
        </>
      )}
    </Box>
  );
};

const arePropsEqual = (
  prevProps: AdSetStartTimeFieldProps,
  nextProps: AdSetStartTimeFieldProps,
): boolean => {
  const prevRow = prevProps.cellParams.row;
  const nextRow = nextProps.cellParams.row;

  return (
    prevRow.id === nextRow.id &&
    prevRow.start_time === nextRow.start_time
  );
};

export const MemoizedAdSetStartTimeField = memo(
  AdSetStartTimeField,
  arePropsEqual,
);

MemoizedAdSetStartTimeField.displayName = 'MemoizedAdSetStartTimeField';
